import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, DollarSign, Loader2, Search, SlidersHorizontal } from 'lucide-react';

import { ROUTES } from '../../constants/routes';
import { Button } from '../../components/ui/Button';
import {
  BillingCard,
  BillingCardContent,
} from '../../components/billing/BillingCard';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { usePayments } from '../../hooks/usePayments';

/** Filter options for payment history */
const FILTER_OPTIONS = ['All', 'This Month', 'Last 30 Days', 'Failed', 'RollOps', 'Cash'] as const;

/** Payment method filter labels */
const METHOD_FILTERS = new Set(['RollOps', 'Cash']);

function filterPaymentsByOption<T extends { method: string; status: string; created_at: string }>(
  items: T[],
  filter: string
): T[] {
  if (filter === 'All') return items;
  if (filter === 'Failed') {
    return items.filter((p) => p.status === 'failed');
  }
  if (METHOD_FILTERS.has(filter)) {
    return items.filter((p) => p.method === filter);
  }
  const now = new Date();
  if (filter === 'This Month') {
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    return items.filter((p) => {
      const d = new Date(p.created_at);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });
  }
  if (filter === 'Last 30 Days') {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 30);
    return items.filter((p) => new Date(p.created_at) >= cutoff);
  }
  return items;
}

interface CustomFilters {
  dateFrom: string;
  dateTo: string;
  member: string;
  subscriptionType: string;
  minAmount: string;
  maxAmount: string;
  method: string;
}

const EMPTY_CUSTOM_FILTERS: CustomFilters = {
  dateFrom: '',
  dateTo: '',
  member: '',
  subscriptionType: '',
  minAmount: '',
  maxAmount: '',
  method: '',
};

function hasCustomFilters(f: CustomFilters): boolean {
  return Object.values(f).some((v) => v.trim() !== '');
}

function filterPaymentsByCustom<T extends {
  memberName: string;
  subscriptionName: string;
  amount: number;
  method: string;
  created_at: string;
}>(items: T[], filters: CustomFilters): T[] {
  if (!hasCustomFilters(filters)) return items;
  return items.filter((p) => {
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      const paymentDate = new Date(p.created_at);
      if (paymentDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      const paymentDate = new Date(p.created_at);
      if (paymentDate > toDate) return false;
    }
    if (filters.member.trim()) {
      const q = filters.member.trim().toLowerCase();
      if (!p.memberName.toLowerCase().includes(q)) return false;
    }
    if (filters.subscriptionType.trim()) {
      const q = filters.subscriptionType.trim().toLowerCase();
      if (!p.subscriptionName.toLowerCase().includes(q)) return false;
    }
    if (filters.minAmount.trim()) {
      const min = parseFloat(filters.minAmount.replace(/[^0-9.-]/g, ''));
      if (!Number.isNaN(min) && p.amount < min) return false;
    }
    if (filters.maxAmount.trim()) {
      const max = parseFloat(filters.maxAmount.replace(/[^0-9.-]/g, ''));
      if (!Number.isNaN(max) && p.amount > max) return false;
    }
    if (filters.method.trim()) {
      const q = filters.method.trim().toLowerCase();
      if (!p.method.toLowerCase().includes(q)) return false;
    }
    return true;
  });
}

/**
 * BillingPayments - Full payment history page for owners.
 * Displays recent purchases with search and filter.
 */
const PAGE_SIZE = 15;

export default function BillingPayments() {
  const navigate = useNavigate();
  const { data: companyId } = useCurrentCompany();
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;
  const {
    data: paymentsData,
    isLoading: isLoadingPayments,
  } = usePayments({
    limit: PAGE_SIZE,
    offset,
    enabled: !!companyId,
  });
  const payments = paymentsData?.payments ?? [];
  const hasNextPage = paymentsData?.hasNextPage ?? false;
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [customFilters, setCustomFilters] = useState<CustomFilters>(EMPTY_CUSTOM_FILTERS);
  const [appliedCustomFilters, setAppliedCustomFilters] = useState<CustomFilters>(EMPTY_CUSTOM_FILTERS);

  const filteredPayments = useMemo(() => {
    if (!payments) return [];
    let result = payments;
    const q = searchValue.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (p) =>
          p.memberName.toLowerCase().includes(q) ||
          p.subscriptionName.toLowerCase().includes(q),
      );
    }
    result = filterPaymentsByOption(result, activeFilter);
    result = filterPaymentsByCustom(result, appliedCustomFilters);
    return result;
  }, [payments, searchValue, activeFilter, appliedCustomFilters]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target as Node)) {
        setShowFilterPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchValue, activeFilter, appliedCustomFilters]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Page Header - matches Billing & Subscriptions styling */}
      <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">
            Payment History
          </h1>
          <button
            type="button"
            onClick={() => navigate(ROUTES.BILLING_UNIFIED)}
            className="flex items-center gap-2 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Billing
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 bg-[#111010] overflow-auto scrollbar-styled min-h-0 py-6 flex flex-col gap-4">
        {/* Search and Filters - outside card */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A3A3]" />
            <input
              type="text"
              placeholder="Search payments..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full h-9 pl-10 pr-4 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
            />
          </div>
          <div className="relative shrink-0" ref={filterPanelRef}>
            <Button
              variant="outline"
              size="sm"
              className={`border-[#504A4A] text-[#A9A3A3] hover:bg-white/5 hover:text-[#EBFFEE] ${
                showFilterPanel ? 'border-green-700 text-green-700' : ''
              }`}
              onClick={() => setShowFilterPanel(!showFilterPanel)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filter
            </Button>
              {showFilterPanel && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-[#221F1F] border border-[#504A4A] rounded-lg shadow-xl z-50 p-4 space-y-4">
                  <h4 className="text-sm font-medium text-[#EBFFEE]">Custom filters</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-white mb-1">Date from</label>
                      <input
                        type="date"
                        value={customFilters.dateFrom}
                        onChange={(e) =>
                          setCustomFilters((f) => ({ ...f, dateFrom: e.target.value }))
                        }
                        className="w-full h-9 px-3 bg-[#111010] border border-[#504A4A] rounded-lg text-white text-sm focus:outline-none focus:border-green-700 [color-scheme:dark]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white mb-1">Date to</label>
                      <input
                        type="date"
                        value={customFilters.dateTo}
                        onChange={(e) =>
                          setCustomFilters((f) => ({ ...f, dateTo: e.target.value }))
                        }
                        className="w-full h-9 px-3 bg-[#111010] border border-[#504A4A] rounded-lg text-white text-sm focus:outline-none focus:border-green-700 [color-scheme:dark]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white mb-1">Member</label>
                      <input
                        type="text"
                        placeholder="Search by member name..."
                        value={customFilters.member}
                        onChange={(e) =>
                          setCustomFilters((f) => ({ ...f, member: e.target.value }))
                        }
                        className="w-full h-9 px-3 bg-[#111010] border border-[#504A4A] rounded-lg text-white placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white mb-1">Subscription type</label>
                      <input
                        type="text"
                        placeholder="Monthly, Punch Card, Drop-In..."
                        value={customFilters.subscriptionType}
                        onChange={(e) =>
                          setCustomFilters((f) => ({ ...f, subscriptionType: e.target.value }))
                        }
                        className="w-full h-9 px-3 bg-[#111010] border border-[#504A4A] rounded-lg text-white placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-[#A9A3A3] mb-1">Min amount</label>
                        <input
                          type="text"
                          placeholder="$0"
                          value={customFilters.minAmount}
                          onChange={(e) =>
                            setCustomFilters((f) => ({ ...f, minAmount: e.target.value }))
                          }
                          className="w-full h-9 px-3 bg-[#111010] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#A9A3A3] mb-1">Max amount</label>
                        <input
                          type="text"
                          placeholder="Any"
                          value={customFilters.maxAmount}
                          onChange={(e) =>
                            setCustomFilters((f) => ({ ...f, maxAmount: e.target.value }))
                          }
                          className="w-full h-9 px-3 bg-[#111010] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-[#A9A3A3] mb-1">Payment method</label>
                      <input
                        type="text"
                        placeholder="RollOps, Cash..."
                        value={customFilters.method}
                        onChange={(e) =>
                          setCustomFilters((f) => ({ ...f, method: e.target.value }))
                        }
                        className="w-full h-9 px-3 bg-[#111010] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5"
                      onClick={() => {
                        setCustomFilters(EMPTY_CUSTOM_FILTERS);
                        setAppliedCustomFilters(EMPTY_CUSTOM_FILTERS);
                        setPage(1);
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-700 hover:bg-green-800 text-white"
                      onClick={() => {
                        setAppliedCustomFilters(customFilters);
                        setActiveFilter('All');
                        setShowFilterPanel(false);
                        setPage(1);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {FILTER_OPTIONS.map((option) => (
              <Button
                key={option}
                variant={activeFilter === option ? 'default' : 'outline'}
                size="sm"
                className={
                  activeFilter === option
                    ? 'bg-green-700 hover:bg-green-800 text-white'
                    : 'border-[#504A4A] text-[#A9A3A3] hover:bg-white/5 hover:text-[#EBFFEE]'
                }
                onClick={() => setActiveFilter(option)}
              >
                {option}
              </Button>
            ))}
        </div>

        <BillingCard className="flex flex-col">
          <BillingCardContent className="flex flex-col">
            {/* Card header: title */}
            <div className="flex items-center gap-2 border-b border-[#504A4A] p-4">
              <DollarSign className="w-5 h-5 text-white" />
              <h2 className="text-lg font-semibold text-[#EBFFEE]">All Payments</h2>
            </div>

            {/* Payments table - height fits content */}
            <div className="overflow-x-auto scrollbar-styled">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#221F1F] z-10">
                  <tr className="border-b border-[#504A4A]">
                    <th className="text-left py-3 px-4 md:px-6 text-sm font-medium text-[#A9A3A3]">
                      Member
                    </th>
                    <th className="text-left py-3 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden sm:table-cell">
                      Subscription
                    </th>
                    <th className="text-right py-3 px-4 md:px-6 text-sm font-medium text-[#A9A3A3]">
                      Amount
                    </th>
                    <th className="text-left py-3 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden md:table-cell">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden md:table-cell">
                      Method
                    </th>
                    <th className="text-left py-3 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden lg:table-cell">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingPayments ? (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-[#9B1421] mx-auto" />
                      </td>
                    </tr>
                  ) : filteredPayments.length > 0 ? (
                    filteredPayments.map((payment) => (
                      <tr
                        key={payment.id}
                        className={`border-b border-[#504A4A]/50 last:border-b-0 hover:bg-white/[0.02] transition-colors ${
                          payment.status === 'failed' ? 'bg-red-950/20' : ''
                        }`}
                      >
                        <td className="py-3 px-4 md:px-6 text-sm text-[#EBFFEE]">
                          {payment.memberName}
                        </td>
                        <td className="py-3 px-4 md:px-6 text-sm text-[#A9A3A3] hidden sm:table-cell">
                          {payment.subscriptionName}
                        </td>
                        <td className="py-3 px-4 md:px-6 text-right text-sm font-medium text-[#EBFFEE]">
                          ${payment.amount.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 md:px-6 text-sm hidden md:table-cell">
                          {payment.status === 'failed' ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/40 text-red-400" title={payment.declineReason}>
                              Failed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-400">
                              Paid
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 md:px-6 text-sm text-[#A9A3A3] hidden md:table-cell">
                          {payment.method}
                        </td>
                        <td className="py-3 px-4 md:px-6 text-sm text-[#A9A3A3] hidden lg:table-cell">
                          {payment.date}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-16 text-center text-sm text-[#A9A3A3]">
                        {searchValue.trim() || activeFilter !== 'All' || hasCustomFilters(appliedCustomFilters)
                          ? 'No payments match your search or filters'
                          : 'No payment history'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!isLoadingPayments && (payments.length > 0 || page > 1) && (
              <div className="flex items-center justify-between gap-4 border-t border-[#504A4A] px-4 py-3 mt-auto">
                <p className="text-sm text-[#A9A3A3]">
                  Page {page}
                  {filteredPayments.length > 0 && (
                    <span className="ml-1">
                      ({filteredPayments.length} on this page)
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#504A4A] text-[#A9A3A3] hover:bg-white/5 hover:text-[#EBFFEE] disabled:opacity-50"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#504A4A] text-[#A9A3A3] hover:bg-white/5 hover:text-[#EBFFEE] disabled:opacity-50"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasNextPage}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </BillingCardContent>
        </BillingCard>
      </div>
    </div>
  );
}
